// Iter 82.3 / Phase E1.3 — Balance Report.
// Reporting view of leave balances (distinct from operational
// /admin/leave/balances). Adds summary rollups and XLSX export.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FunnelSimple, FileXls } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function LeaveBalanceReport() {
    const [fys, setFys] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [policies, setPolicies] = useState([]);
    const [filters, setFilters] = useState({ fiscal_year: "", department: "", leave_type_code: "" });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fy, setFy] = useState("");

    const loadMeta = useCallback(async () => {
        try {
            const [fyResp, hr, pl] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/hr/employee/meta"),
                api.get("/leave/policy/list"),
            ]);
            setFys(fyResp.data.items || []);
            setHrMeta(hr.data);
            setPolicies(pl.data.items || []);
        } catch (err) { toast.error(formatApiError(err, "Failed to load meta")); }
    }, []);

    const effectiveFy = useMemo(() => {
        if (filters.fiscal_year) return filters.fiscal_year;
        const current = fys.find((f) => f.is_current);
        return current?.code || "";
    }, [filters.fiscal_year, fys]);

    const reload = useCallback(async () => {
        if (!effectiveFy) return;
        setLoading(true);
        try {
            const params = { fiscal_year: effectiveFy };
            if (filters.department) params.department = filters.department;
            if (filters.leave_type_code) params.leave_type_code = filters.leave_type_code;
            const { data } = await api.get("/leave/balance/list", { params });
            setRows(data.items || []);
            setFy(data.fiscal_year || effectiveFy);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load balances"));
        } finally {
            setLoading(false);
        }
    }, [effectiveFy, filters.department, filters.leave_type_code]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const summary = useMemo(() => {
        const total_employees = new Set(rows.map((r) => r.employee_id)).size;
        const total_balance = rows.reduce((acc, r) => acc + Number(r.current_balance || 0), 0);
        const total_taken = rows.reduce((acc, r) => acc + Number(r.total_taken_ytd || 0), 0);
        return {
            total_rows: rows.length, total_employees,
            total_balance: total_balance.toFixed(2),
            total_taken: total_taken.toFixed(2),
        };
    }, [rows]);

    const download = async (ext) => {
        try {
            const params = { fiscal_year: effectiveFy };
            if (filters.department) params.department = filters.department;
            if (filters.leave_type_code) params.leave_type_code = filters.leave_type_code;
            const path = ext === "xlsx" ? "/leave/report/balance/export.xlsx" : "/leave/balance/export";
            const { data: blob } = await api.get(path, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-balance-${fy}-${new Date().toISOString().slice(0, 10)}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-balance-report-page">
            <PageHeader
                eyebrow="Leave Reports"
                title="Balance Report"
                description={`Reporting view of leave balances for FY ${fy}.`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="br-export-csv">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="br-export-xlsx">
                            <FileXls size={14} className="mr-1" /> Excel
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="br-summary">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Balance Rows</div>
                    <div className="text-3xl font-bold text-slate-900 mt-1">{summary.total_rows}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Distinct Employees</div>
                    <div className="text-3xl font-bold text-slate-900 mt-1">{summary.total_employees}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Total Available</div>
                    <div className="text-3xl font-bold text-emerald-900 mt-1">{summary.total_balance}</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-rose-800 font-semibold">Total Taken YTD</div>
                    <div className="text-3xl font-bold text-rose-900 mt-1">{summary.total_taken}</div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="br-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Fiscal Year</label>
                        <select data-testid="br-filter-fy" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={effectiveFy} onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}>
                            {fys.map((f) => (<option key={f.id} value={f.code}>{f.code}{f.is_current ? " (current)" : ""}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select data-testid="br-filter-dept" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (<option key={d.id || d.name} value={d.name}>{d.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select data-testid="br-filter-type" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.leave_type_code} onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value })}>
                            <option value="">All</option>
                            {policies.map((p) => (<option key={p.code} value={p.code}>{p.code}</option>))}
                        </select>
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="br-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Dept</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-right px-3 py-2 font-medium">Opening</th>
                                <th className="text-right px-3 py-2 font-medium">Accrued</th>
                                <th className="text-right px-3 py-2 font-medium">Taken</th>
                                <th className="text-right px-3 py-2 font-medium">Encashed</th>
                                <th className="text-right px-3 py-2 font-medium">Current</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading…</td></tr>}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No balances match these filters.</td></tr>
                            )}
                            {!loading && rows.map((r) => {
                                const emp = r.employee || {};
                                return (
                                    <tr key={`${r.employee_id}-${r.leave_type_code}`} className="border-t border-slate-100" data-testid={`br-row-${r.employee_id}-${r.leave_type_code}`}>
                                        <td className="px-3 py-2">
                                            <Link to={`/admin/leave/balances/${r.employee_id}?fy=${r.fiscal_year}`} className="hover:underline">
                                                <div className="font-medium text-slate-900">{emp.full_name || r.employee_id}</div>
                                                <div className="text-xs text-slate-500 font-mono">{emp.employee_code}</div>
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{emp.department || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                        <td className="px-3 py-2 text-right">{(r.opening_balance ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-emerald-700">{(r.total_accrued_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-rose-700">{(r.total_taken_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-amber-700">{(r.total_encashed_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{(r.current_balance ?? 0).toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
